/**
 * PORTED — RO-3-11 / #44304.
 * Origin: `excellent/app/database/editor/[table]/_client.tsx` (verbatim, minus
 * the inert `"use client"`).
 *
 * Zero `next/*` edges and zero server edges — it was already a pure composition
 * over four `packages/ui` blocks, and all of their contracts (`rows.*`,
 * `assistant.chat`, plus `TableSwitcher`'s prop-driven inventory) are mounted, so
 * the row editor is live in this shell.
 *
 * `writeMode` deliberately resets to `false` on every mount: read-only on every
 * fresh load is a stated invariant (docs/database/PRD.md §13.2), and it survives
 * the port because it was never persisted anywhere to begin with.
 */

import * as React from "react";
import {
  WriteModeToggle,
  DatabaseGrid,
  TableSwitcher,
  DatabaseAssistant,
  Card,
  Button,
} from "@excellent/ui";
import { Icon } from "@excellent/ui/icons";
import type { DbObjectDto } from "@excellent/ui/api/contracts/database/schema";

/**
 * Table Editor client island — owns the per-session write-mode state and feeds
 * it to the grid. Read-only on every fresh load (PRD §13.2). The grid is ONE
 * integrated surface: the `TableSwitcher` is its header title (jump tables in
 * place), and "Ask AI about this data" rides the header (not a detached footer),
 * toggling a docked Assistant panel below.
 */
export function TableEditorClient({
  table,
  objects,
  initialFilters,
}: {
  table: string;
  objects: DbObjectDto[];
  initialFilters?: Record<string, string>;
}) {
  const [writeMode, setWriteMode] = React.useState(false);
  const [askOpen, setAskOpen] = React.useState(false);

  return (
    <div className="flex flex-col gap-4">
      <WriteModeToggle writeMode={writeMode} onChange={setWriteMode} />
      <DatabaseGrid
        table={table}
        writeMode={writeMode}
        initialFilters={initialFilters}
        title={
          <TableSwitcher objects={objects} current={table} trigger="title" />
        }
        headerActions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAskOpen((o) => !o)}
          >
            <Icon name="sparkles" size="sm" />
            {askOpen ? "Hide assistant" : "Ask AI about this data"}
          </Button>
        }
      />
      {askOpen ? (
        <Card className="max-h-[32rem] overflow-y-auto p-4">
          <DatabaseAssistant mode="panel" table={table} />
        </Card>
      ) : null}
    </div>
  );
}
